import { Component, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import type { AbstractControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { GeolocationService } from '@ng-web-apis/geolocation';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { CoordinateDialogComponent } from '../set-detail/coordinate-dialog/coordinate-dialog.component';
import { ConfirmDialogComponent } from '../vessel-reg/confirm-dialog/confirm-dialog.component';
import { Coordinate } from '../shared/coordinate.model';
import { LookupDataService } from '../shared/lookup-data.service';
import { LookupItem } from '../shared/lookup-item.model';
import { warnOnPoorPositionAccuracy } from '../shared/position-accuracy';

/**
 * The data collected by {@link HmsInportDialogComponent}. A single in-port set is
 * generated for every day in the [start, end] range, all sharing the gear info below.
 */
export interface HmsInPortResult {
    start: Date;
    end: Date;
    gearType: string;
    gearSubtype: LookupItem | null;
    buoyCount: number | null;
    hookCount: number | null;
    aircraftUsed: boolean | null;
    lat: Coordinate;
    lon: Coordinate;
    /** Accuracy in metres when lat/lon were auto-filled from the device; null for manual entry. */
    positionAccuracy: number | null;
}

/**
 * Dialog to bulk-create HMS "in port" daily fishing log entries over a date range.
 * The fisher picks a date range plus the gear (and gear-dependent required fields);
 * one set per day is generated with fishing mode "In Port" and 0 hours fished.
 */
@Component({
    selector: 'app-hms-inport-dialog',
    templateUrl: './hms-inport-dialog.component.html',
    styleUrl: './hms-inport-dialog.component.css',
    standalone: false,
})
export class HmsInportDialogComponent implements OnDestroy {
    gearOptions: string[] = LookupDataService._HmsGearTypes.map((g) => g.name);
    gearSubtypes: LookupItem[] = LookupDataService._HmsDBSGTypes;

    range: FormGroup = new FormGroup({
        start: new FormControl<Date | null>(null, [Validators.required]),
        end: new FormControl<Date | null>(null, [Validators.required]),
    });

    gearForm: FormGroup = new FormGroup({
        gear: new FormControl<string>('', [Validators.required]),
        dsbgType: new FormControl<LookupItem | null>(null),
        numBuoys: new FormControl<number | null>(null),
        numHooks: new FormControl<number | null>(null),
        aircraftUsed: new FormControl<boolean | null>(null),
    });

    // Location is optional for an in-port day; the same coordinates apply to every generated set.
    lat: Coordinate = new Coordinate();
    lon: Coordinate = new Coordinate();

    private position?: GeolocationCoordinates;
    private locationSubscription: Subscription = new Subscription();

    // Accuracy of the auto-filled coordinates above; cleared when they are entered by hand.
    private positionAccuracy?: number;

    constructor(
        public dialogRef: MatDialogRef<HmsInportDialogComponent>,
        private dialog: MatDialog,
        private readonly geolocation$: GeolocationService,
        private toastr: ToastrService,
    ) {
        this.gearForm.get('gear')!.valueChanges.subscribe((gear) => this.handleGearChange(gear ?? ''));

        this.locationSubscription = this.geolocation$.subscribe(
            (pos) => (this.position = pos.coords),
            (err) => {
                // User has denied permission
                if (err.code === 1) {
                    this.toastr.warning('Allow geolocation permission to access device location', 'Location Permission Denied');
                }
            },
        );
    }

    /**
     * Autofill latitude/longitude from the device's current location, mirroring the HMS set screen.
     * Confirms before overwriting coordinates that have already been entered.
     */
    fillLocation() {
        if (!this.position) {
            this.toastr.warning('Allow geolocation permission to access device location', 'Location Unavailable');
            return;
        }

        const doFill = () => {
            const pos = this.position!;
            this.lat = Coordinate.fillDegrees(pos.latitude, pos.latitude < 0 ? 'S' : 'N');
            this.lon = Coordinate.fillDegrees(pos.longitude, pos.longitude < 0 ? 'W' : 'E');
            //Record how good the device thought this fix was, so bad positions can be identified later
            this.positionAccuracy = Math.round(pos.accuracy);
            warnOnPoorPositionAccuracy(this.toastr, this.positionAccuracy);
        };

        if (this.lat.isUnfilled() && this.lon.isUnfilled()) {
            doFill();
            return;
        }

        this.dialog
            .open(ConfirmDialogComponent, { data: { screen: 'autofill' } })
            .afterClosed()
            .subscribe((result) => {
                if (result) doFill();
            });
    }

    /**
     * Enter latitude/longitude via the shared coordinate dialog (same one used on the HMS set screen).
     */
    openCoordinateDialog(title: 'lat' | 'lon') {
        const current = title === 'lat' ? this.lat : this.lon;
        const dialogRef = this.dialog.open(CoordinateDialogComponent, {
            width: '95vw',
            maxWidth: '500px',
            data: { title, coord: current, fishery: 'HMS' },
        });

        dialogRef.afterClosed().subscribe((result: Coordinate | undefined) => {
            if (!result) return;
            if (!result.isUnfilled()) result.fillDecimal();
            if (title === 'lat') this.lat = result;
            else this.lon = result;
            //A hand-edited coordinate is no longer a device reading, so its accuracy no longer applies
            this.positionAccuracy = undefined;
        });
    }

    /**
     * Strip non-numeric characters as the user types
     */
    numericOnly(control: AbstractControl) {
        control.setValue(String(control.value ?? '').replace(/[^0-9]/g, ''), { onlySelf: true, emitEvent: false });
    }

    compareDSBGtype = (o1: LookupItem, o2: LookupItem): boolean => {
        return o1 && o2 ? o1.name === o2.name : o1 === o2;
    };

    get isDSBG(): boolean {
        return this.gearForm.get('gear')?.value === 'Deep Set Buoy Gear';
    }

    get isHarpoon(): boolean {
        return this.gearForm.get('gear')?.value === 'Harpoon';
    }

    /**
     * Mirror the set-detail validation rules: DSBG requires a subtype plus buoy/hook
     * counts, Harpoon requires the aircraft-used indicator. Reset the dependent fields
     * whenever the gear changes so stale values can't be submitted.
     */
    handleGearChange(gear: string) {
        const dsbgType = this.gearForm.get('dsbgType');
        const numBuoys = this.gearForm.get('numBuoys');
        const numHooks = this.gearForm.get('numHooks');
        const aircraftUsed = this.gearForm.get('aircraftUsed');

        if (gear === 'Deep Set Buoy Gear') {
            dsbgType?.setValidators(Validators.required);
            numBuoys?.setValidators([Validators.required, Validators.min(0), Validators.max(10)]);
            numHooks?.setValidators([Validators.required, Validators.min(0), Validators.max(30)]);
        } else {
            dsbgType?.clearValidators();
            numBuoys?.clearValidators();
            numHooks?.clearValidators();
        }

        if (gear === 'Harpoon') aircraftUsed?.setValidators(Validators.required);
        else aircraftUsed?.clearValidators();

        dsbgType?.setValue(null, { emitEvent: false });
        numBuoys?.setValue(null, { emitEvent: false });
        numHooks?.setValue(null, { emitEvent: false });
        // Default the aircraft indicator to "No" for harpoon (sensible for an in-port day, still editable)
        aircraftUsed?.setValue(gear === 'Harpoon' ? false : null, { emitEvent: false });

        [dsbgType, numBuoys, numHooks, aircraftUsed].forEach((c) => c?.updateValueAndValidity({ emitEvent: false }));
    }

    /**
     * Validate both forms, then close with the collected {@link HmsInPortResult}.
     */
    confirm() {
        this.range.markAllAsTouched();
        this.gearForm.markAllAsTouched();

        if (this.range.invalid) {
            this.toastr.error('Please select a valid start and end date', 'Invalid date range');
            return;
        }

        const start: Date = this.range.get('start')!.value;
        const end: Date = this.range.get('end')!.value;
        if (end < start) {
            this.toastr.error('End date must be on or after the start date', 'Invalid date range');
            return;
        }

        if (this.gearForm.invalid) {
            this.toastr.error('Please complete all required gear fields', 'Missing information');
            return;
        }

        const value = this.gearForm.value;
        const result: HmsInPortResult = {
            start,
            end,
            gearType: value.gear ?? '',
            gearSubtype: this.isDSBG ? (value.dsbgType ?? null) : null,
            buoyCount: this.isDSBG ? Number(value.numBuoys) : null,
            hookCount: this.isDSBG ? Number(value.numHooks) : null,
            aircraftUsed: this.isHarpoon ? !!value.aircraftUsed : null,
            lat: this.lat,
            lon: this.lon,
            positionAccuracy: this.positionAccuracy ?? null,
        };

        this.dialogRef.close(result);
    }

    onNoClick(): void {
        this.dialogRef.close();
    }

    ngOnDestroy(): void {
        this.locationSubscription.unsubscribe();
    }
}
